window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(async (swat) => {
  class Wishlist {
    constructor() {
      const queryParams = swat.utils.getEncodedAsObject(window.location.search);

      this.initialized = false;
      this.id = queryParams["hkey"] || queryParams["lid"] || null;
      this.items = [];
      this.isShared = window.location.pathname.includes("swym-share-wishlist");

      this.init();
    }

    async init() {
      try {
        if (this.isShared && this.id) {
          this.items = await this.fetchListCtx();
          this.initialized = true;
          document.dispatchEvent(new CustomEvent("wishlist:updated"));
          return;
        }
        const lists = await this.fetchLists();
        this.initialized = true;
        this.id = lists[0].lid;
        this.items = lists[0].listcontents || [];
        document.dispatchEvent(new CustomEvent("wishlist:updated"));
      } catch (error) {
        return console.error("Error while fetching lists", error);
      }
    }

    fetchLists() {
      return new Promise((resolve, reject) => {
        swat.fetchLists({
          callbackFn: async (_lists) => {
            if (!_lists.length) {
              try {
                resolve(await this.createList());
              } catch (error) {
                reject(error);
              }
            }
            resolve(_lists);
          },
          errorFn: (error) => reject(error),
        });
      });
    }

    fetchListCtx() {
      return new Promise((resolve, reject) => {
        swat.fetchListCtx(
          { lid: this.id },
          (contents) => resolve(contents),
          (error) => reject(error)
        );
      });
    }

    createList() {
      return new Promise((resolve, reject) => {
        swat.createList(
          { lname: "Wishlist" },
          (list) => resolve([list]),
          (error) => reject(error)
        );
      });
    }

    generateSharedListURL() {
      return new Promise((resolve, reject) => {
        swat.generateSharedListURL(
          this.id,
          (url) => resolve(url),
          (error) => reject(error)
        );
      });
    }

    shareListSocial(fromName, platform, note) {
      const platformShareUrlTemplate =
        swat.retailerSettings.Wishlist.SharingModes.find(
          ({ type }) => type === platform
        ).url;
      swat.shareListSocial(
        this.id,
        fromName,
        platformShareUrlTemplate,
        platform,
        note,
        (error) => reject(error)
      );
    }

    add(products, callback = null) {
      swat.addProductsToList(
        this.id,
        products,
        async () => {
          try {
            const lists = await this.fetchLists();
            this.id = lists[0].lid;
            this.items = lists[0].listcontents || [];
            if (typeof callback === "function") {
              callback();
            } else {
              Toastify({
                text: window.languages.wishlist.added,
                duration: 3000,
              }).showToast();
              document.dispatchEvent(new CustomEvent("wishlist:updated"));
            }
          } catch (error) {
            console.error("Error while adding products", error);
          }
        },
        (error) => {
          console.error("Error while adding products", error);
        }
      );
    }

    remove(products, callback = null) {
      swat.removeProductsFromList(
        this.id,
        products,
        async () => {
          try {
            const lists = await this.fetchLists();
            this.id = lists[0].lid;
            this.items = lists[0].listcontents || [];
            if (typeof callback === "function") {
              callback();
            } else {
              Toastify({
                text: window.languages.wishlist.removed,
                duration: 3000,
              }).showToast();
              document.dispatchEvent(new CustomEvent("wishlist:updated"));
            }
          } catch (error) {
            console.error("Error while adding product", error);
          }
        },
        (error) => {
          console.error("Error while adding product", error);
        }
      );
    }
  }

  const wishlist = new Wishlist();

  class WishlistToggle extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
    }

    connectedCallback() {
      this.button = this.querySelector("button");
      if (!this.button) return;

      this.button.addEventListener("click", this.onClick.bind(this));
      document.addEventListener("wishlist:updated", this.update.bind(this));
      this.update();
    }

    update() {
      if (!wishlist.initialized) return;
      if (this.loading) return;

      this.button.disabled = false;

      this.isInWishlist = wishlist.items.some(
        (item) => item.empi == this.dataset.productId
      );

      const label = this.isInWishlist
        ? window.languages.wishlist.remove
        : window.languages.wishlist.add;
      this.button.hasAttribute("aria-label")
        ? this.button.setAttribute("aria-label", label)
        : (this.button.innerText = label);
      this.button.classList.toggle("is-in-wishlist", this.isInWishlist);
    }

    onClick() {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
        this.update();
      }, 2000);

      this.button.disabled = true;

      const label = this.isInWishlist
        ? window.languages.wishlist.removed
        : window.languages.wishlist.added;
      this.button.hasAttribute("aria-label")
        ? this.button.setAttribute("aria-label", label)
        : (this.button.innerText = label);
      this.button.classList.toggle("is-in-wishlist", !this.isInWishlist);

      const products = [
        {
          empi: this.dataset.productId,
          epi: this.dataset.variantId,
          du: this.dataset.productUrl,
        },
      ];
      this.isInWishlist ? wishlist.remove(products) : wishlist.add(products);
    }
  }

  customElements.define("wishlist-toggle", WishlistToggle);

  class WishlistItems extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      document.addEventListener("wishlist:updated", this.update.bind(this));
      this.update();
    }

    async update() {
      if (!wishlist.initialized) return;

      if (wishlist.items.length === 0) {
        this.innerHTML = `
          <div class="Container">
            <div class="PageContent PageContent--center PageContent--narrow Rte">
              <p class="u-h4">${window.languages.wishlist.empty}</p>
              <a href="${window.routes.all_shoes_collection_url}" class="Button Button--primary">${window.languages.wishlist.continueShopping}</a>
            </div>
          </div>
        `;
        return;
      }

      const products = await this.fetchProducts();
      const productsToRender = products.filter(Boolean);

      // Remove any products that failed to load
      if (productsToRender.length !== products.length) {
        const productsToRemove = products
          .map((product, index) => (product ? null : index))
          .filter(Boolean);
        wishlist.remove(
          productsToRemove.map((index) => ({
            empi: wishlist.items[index].empi,
            epi: wishlist.items[index].epi,
            du: wishlist.items[index].du,
          })),
          () => document.dispatchEvent(new CustomEvent("wishlist:updated"))
        );
      }

      const productsHTML = productsToRender.join("");

      if (!productsHTML) {
        this.innerHTML = `
          <div class="Container">
            <div class="PageContent PageContent--center PageContent--narrow Rte">
              <p class="u-h4">${window.languages.wishlist.empty}</p>
              <a href="${window.routes.all_shoes_collection_url}" class="Button Button--primary">${window.languages.wishlist.continueShopping}</a>
            </div>
          </div>
        `;
        return;
      }

      this.innerHTML = `
        <div class="ProductListWrapper">
          <div class="ProductList ProductList--grid ProductList--removeMargin Grid collection">
            ${productsHTML}
          </div>
        </div>
        <div class="Container">
          <div class="PageContent PageContent--center PageContent--narrow Rte">
            <a href="${window.routes.all_shoes_collection_url}" class="Button Button--primary">${window.languages.wishlist.continueShopping}</a>
          </div>
        </div>
      `;

      const productForms = this.querySelectorAll(".ProductItemForm");
      productForms.forEach((form) => {
        form.addEventListener("change", () => {
          const submitButton = form.querySelector("button[type=submit]");
          if (!submitButton) return;
          submitButton.disabled = !form.checkValidity();
        });
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          const products = [
            {
              empi: form.dataset.productId,
              epi: form.dataset.variantId,
              du: form.dataset.productUrl,
            },
          ];
          wishlist.remove(products, () => form.submit());
          const submitButton = form.querySelector("button[type=submit]");
          if (!submitButton) return;
          submitButton.disabled = true;
        });
      });
    }

    fetchProducts() {
      return Promise.all(
        wishlist.items.map(async (item) => {
          try {
            const url = new URL(
              item.du.replace(window.theme.permanentDomain, window.theme.domain)
            );
            url.searchParams.set("view", "wishlist");
            const response = await fetch(url.toString());
            if (!response.ok) throw new Error(response.statusText);
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const wishlistItem = doc.querySelector(".WishlistItem");
            if (!wishlistItem) throw new Error("Product not found");
            return html;
          } catch (error) {
            console.error("Error while fetching product", error);
            return "";
          }
        })
      );
    }
  }

  customElements.define("wishlist-items", WishlistItems);

  class WishlistIcon extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      document.addEventListener("wishlist:updated", this.update.bind(this));
      this.update();
    }

    update() {
      if (!wishlist.initialized) return;

      this.classList.toggle("has-items", wishlist.items.length > 0);
    }
  }

  customElements.define("wishlist-icon", WishlistIcon);

  class WishlistCount extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      document.addEventListener("wishlist:updated", this.update.bind(this));
      this.update();
    }

    update() {
      if (!wishlist.initialized) return;

      this.innerHTML =
        wishlist.items.length > 0 ? `(${wishlist.items.length})` : "";
    }
  }

  customElements.define("wishlist-count", WishlistCount);

  class WishlistShareButton extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.button = this.querySelector("button");
      if (!this.button) return;

      this.button.addEventListener("click", this.onClick.bind(this));
    }

    onClick() {
      switch (this.dataset.shareType) {
        case "link":
          this.shareLink();
          break;
        case "facebook":
          this.shareFacebook();
          break;
        case "twitter":
          this.shareTwitter();
          break;
      }
    }

    async shareLink() {
      let url = await wishlist.generateSharedListURL();
      if (this.dataset.shareMessage) {
        url = `${url}&message=${this.dataset.shareMessage}`;
      }
      try {
        await navigator.clipboard.writeText(url);
        this.button.innerText = window.languages.wishlist.share.copied;
        setTimeout(() => {
          this.button.innerText = window.languages.wishlist.share.copyLink;
        }, 2000);
      } catch (error) {
        console.error("Error while copying to clipboard", error);
      }
    }

    shareFacebook() {
      wishlist.shareListSocial(
        this.dataset.fromName,
        "facebook",
        window.languages.wishlist.share.note
      );
    }

    shareTwitter() {
      wishlist.shareListSocial(
        this.dataset.fromName,
        "twitter",
        window.languages.wishlist.share.note
      );
    }
  }

  customElements.define("wishlist-share-button", WishlistShareButton);

  class WishlistShareMessage extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const queryParams = swat.utils.getEncodedAsObject(window.location.search);
      if (!queryParams["message"]) return;

      this.innerHTML = `<p>${queryParams["message"]}</p>`;
    }
  }

  customElements.define("wishlist-share-message", WishlistShareMessage);
});
